/**
 * Page Profile Style Generation Stage
 *
 * Generates global styles from structured product data.
 * Uses Haiku for fast, non-streaming generation.
 */

import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import type { PageProfileInput } from '../types/page-profile';
import type { ModelOption, AiGenerationStats } from '../types';
import { nonStreamingCompletion, extractJsonFromResponse } from '../api/anthropic-client';
import { buildPageProfileStylePrompt } from '../prompts/page-profile-style-prompt';

export interface PageProfileStyleResult {
  style: StylesConfigDto;
  label: string;
}

const MAX_TOKENS = 8000;
const MAX_RETRIES = 1;

/**
 * Generate global styles from structured product data
 *
 * @param input - The PageProfileInput containing product data
 * @param signal - Optional AbortSignal for cancellation
 * @returns PageProfileStyleResult containing the generated style and label
 */
export const generatePageProfileStyle = async (
  input: PageProfileInput,
  signal?: AbortSignal,
  model?: ModelOption,
  stats?: AiGenerationStats,
): Promise<PageProfileStyleResult> => {
  let lastResponseText = '';

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const { system, user } = buildPageProfileStylePrompt(input, attempt > 0);

    console.log('[PageProfile Style] Generating style...');
    console.log('[PageProfile Style] System prompt:', system);
    console.log('[PageProfile Style] User prompt:', user);
    const t0 = Date.now();
    const result = await nonStreamingCompletion({
      model: model ?? 'haiku-4.5',
      maxTokens: MAX_TOKENS,
      systemPrompt: system,
      userPrompt: user,
      signal,
    });
    if (stats) {
      const durationMs = Date.now() - t0;
      stats.totalInputTokens += result.inputTokens;
      stats.totalOutputTokens += result.outputTokens;
      stats.trackStage?.('style', {
        model: model ?? 'haiku-4.5',
        inputTokens: result.inputTokens,
        outputTokens: result.outputTokens,
        durationMs,
      });
    }

    lastResponseText = result.text;

    try {
      const jsonText = extractJsonFromResponse(result.text);
      const parsed = JSON.parse(jsonText) as PageProfileStyleResult;

      if (!parsed.label || !parsed.style) {
        throw new Error('Missing label or style in response');
      }

      console.log(`[PageProfile Style] Style generated: "${parsed.label}"`);
      return parsed;
    } catch (error) {
      console.warn(
        `[PageProfile Style] Attempt ${attempt + 1} failed to parse JSON:`,
        error instanceof Error ? error.message : error,
      );
      // Continue to retry
    }
  }

  throw new Error(
    `Style generation failed: the AI did not return valid JSON after ${MAX_RETRIES + 1} attempts. ` +
      `Response started with: "${lastResponseText.slice(0, 80)}..."`,
  );
};
